import React from 'react';
import PropTypes from 'prop-types';
import { deepPure } from '~/util';
import {
  mapIsoStringtoUtcObject,
  mapRepliconDateToMidnightUTCString
} from '~/modules/common/dates/convert';
import { compareISOStrings } from '~/modules/common/dates/compare';
import { convertDurationToHours } from '~/modules/common/util';
import { useMeContext } from '~/modules/me';
import { getTodayForUser } from '~/modules/common/dates/today';
import ResourceActualHoursSummaryBlock from './ResourceActualHoursSummaryBlock';
import useStyles from './useStyles';

export const getAllocationPeriod = ({ period, allocationPeriodMap, scale }) => {
  const actualStartDate = mapIsoStringtoUtcObject(
    period.dateRange.startDate
  ).toISODate();

  return allocationPeriodMap[`${scale}-${actualStartDate}`];
};

export const emptyDateInFuture = ({
  todayDate,
  period,
  isPSARMP2023Q3BugsEnabled
}) => {
  const dateComparedValue = compareISOStrings(
    mapRepliconDateToMidnightUTCString(todayDate),
    period.dateRange.startDate
  );

  return isPSARMP2023Q3BugsEnabled
    ? !(dateComparedValue < 0 && convertDurationToHours(period.hours) === 0)
    : !period.emptyPeriodInFuture;
};

export const ResourceActualHoursSummaryBlocks = ({
  dataQeId,
  chartStartDate,
  scale,
  resourceUser,
  allocationPeriods,
  resourceUserActualTimePeriods,
  resourceAvailabilityTimePeriods,
  cssStyles,
  actualSummaryBlockPopupProps
}) => {
  const classes = useStyles();
  const me = useMeContext();
  const todayDate = getTodayForUser(me);

  const allocationPeriodMap = (allocationPeriods || []).reduce((acc, alloc) => {
    acc[alloc.key] = alloc;

    return acc;
  }, []);

  const {
    featureFlags: { isPSARMP2023Q3BugsEnabled }
  } = me;

  return (
    <div className={classes.timeBlocks}>
      {!resourceUserActualTimePeriods &&
        allocationPeriods &&
        allocationPeriods.map(
          period =>
            compareISOStrings(
              mapRepliconDateToMidnightUTCString(todayDate),
              mapRepliconDateToMidnightUTCString(period.startDate)
            ) > -1 && (
              <ResourceActualHoursSummaryBlock
                key={`${resourceUser.id}-${period.startDate}`}
                dataQeId={`ActualBlock${dataQeId}-${resourceUser.displayText}-${period.startDate}`}
                hours={0}
                startDate={period.startDate}
                endDate={period.endDate}
                chartStartDate={chartStartDate}
                scale={scale}
                allocationPeriod={period}
                resourceAvailabilityTimePeriod={(
                  resourceAvailabilityTimePeriods || []
                ).find(
                  entry =>
                    compareISOStrings(
                      entry.dateRange.startDate,
                      mapRepliconDateToMidnightUTCString(period.startDate)
                    ) === 0
                )}
                zeroHours
                cssStyles={cssStyles}
                actualSummaryBlockPopupProps={actualSummaryBlockPopupProps}
              />
            )
        )}
      {resourceUserActualTimePeriods &&
        resourceUserActualTimePeriods.map(
          period =>
            emptyDateInFuture({
              todayDate,
              period,
              isPSARMP2023Q3BugsEnabled
            }) && (
              <ResourceActualHoursSummaryBlock
                key={`${resourceUser.id}-${period.dateRange.startDate}`}
                dataQeId={`ActualBlock${dataQeId}-${resourceUser.displayText}-${period.dateRange.startDate}`}
                hours={convertDurationToHours(period.hours)}
                actualCost={period.cost}
                startDate={mapIsoStringtoUtcObject(period.dateRange.startDate)}
                endDate={mapIsoStringtoUtcObject(period.dateRange.endDate)}
                chartStartDate={chartStartDate}
                scale={scale}
                allocationPeriod={getAllocationPeriod({
                  period,
                  allocationPeriods,
                  scale,
                  allocationPeriodMap
                })}
                resourceAvailabilityTimePeriod={(
                  resourceAvailabilityTimePeriods || []
                ).find(
                  entry =>
                    compareISOStrings(
                      entry.dateRange.startDate,
                      period.dateRange.startDate
                    ) === 0
                )}
                zeroHours={convertDurationToHours(period.hours) === 0}
                cssStyles={cssStyles}
                actualSummaryBlockPopupProps={actualSummaryBlockPopupProps}
              />
            )
        )}
    </div>
  );
};
ResourceActualHoursSummaryBlocks.propTypes = {
  dataQeId: PropTypes.string,
  chartStartDate: PropTypes.object,
  scale: PropTypes.string,
  allocationPeriods: PropTypes.array,
  resourceUser: PropTypes.object,
  resourceUserActualTimePeriods: PropTypes.array,
  resourceAvailabilityTimePeriods: PropTypes.array,
  cssStyles: PropTypes.object,
  actualSummaryBlockPopupProps: PropTypes.object
};

export default deepPure(ResourceActualHoursSummaryBlocks);
